import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { DropdownList, IconInfo, Stack } from '@adc/parallax-component-library';
import { faker } from '@faker-js/faker';
import { useForm, useStore } from '@tanstack/react-form';
import * as z from 'zod';

import { DropdownInput } from '~/components/DropdownInput';
import { CLMPhoneInput } from '~/components/inputs/CLMPhoneNumberInput';
import { CLMTextInput } from '~/components/inputs/CLMTextInput';
import { CountryListItems, CountryOptions } from '~/constants/forms';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import { idnSchema } from './validations';

export type IDNFormValues = z.infer<typeof idnSchema>;

export type IDNFormRef = {
  submitForm: () => Promise<void>;
};

type IDNFormProps = {
  testID: string;
  onSubmit: (values: IDNFormValues) => Promise<void>;
  defaultValues?: Partial<IDNFormValues>;
};

const baseDefaultValues: IDNFormValues = {
  name: '',
  id: faker.string.numeric(6),
  address: '',
  addressLine2: undefined,
  city: '',
  state: '',
  zipCode: '',
  country: 'US',
  phone: undefined,
};

export const IDNForm = forwardRef<IDNFormRef, IDNFormProps>(
  ({ testID, onSubmit, defaultValues }, ref) => {
    const form = useForm({
      defaultValues: {
        ...baseDefaultValues,
        ...defaultValues,
      },
      validators: { onChange: idnSchema },
      onSubmit: ({ value }) => {
        return onSubmit(idnSchema.parse(value));
      },
    });

    const countryValue = useStore(form.store, (s) => s.values.country);

    const { countrySelectedIndex, selectedCountryLabel } = useMemo(() => {
      const index = Object.keys(CountryOptions).indexOf(
        countryValue as keyof typeof CountryOptions,
      );
      const label = CountryOptions[countryValue as keyof typeof CountryOptions];

      return {
        countrySelectedIndex: index,
        selectedCountryLabel: label,
      };
    }, [countryValue]);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: form.handleSubmit,
      }),
      [form.handleSubmit],
    );

    return (
      <Stack gap="$6" paddingTop="$2" testID={testID}>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="name">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'name'],
                  })}
                  label="IDN Name"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="id">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'id'],
                  })}
                  label="IDN ID"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inlineIconConfig={{
                    icon: <IconInfo />,
                    'aria-label': 'IDN ID informations',
                    onPress: () => {
                      // TODO: add popup when content of it will be provided
                    },
                    disabledColor: '$interactive.primary.disabled.onColor',
                  }}
                  {...getErrorProps(field)}
                  disabled
                />
              )}
            </form.Field>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1}>
            <form.Field name="address">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'address'],
                  })}
                  label="IDN Address"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1}>
            <form.Field name="addressLine2">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'address', 'line2'],
                  })}
                  label="IDN Address Line 2 (Optional)"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1}>
            <form.Field name="city">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'city'],
                  })}
                  label="City"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1}>
            <form.Field name="state">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'state'],
                  })}
                  label="State/Province"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="zipCode">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'zip', 'code'],
                  })}
                  label="Zip Code"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="country">
              {(field) => (
                <DropdownList
                  testID={buildTestId({
                    testID,
                    suffix: ['dropdown', 'country'],
                  })}
                  items={CountryListItems}
                  selectedIndex={countrySelectedIndex}
                  onSelect={(index) => {
                    const selectedCountryCode = Object.keys(CountryOptions)[
                      index
                    ] as keyof typeof CountryOptions;

                    field.setValue(selectedCountryCode);
                  }}
                >
                  <DropdownInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'country'],
                    })}
                    aria-label="Country"
                    label="Country"
                    value={selectedCountryLabel}
                    {...getErrorProps(field)}
                  />
                </DropdownList>
              )}
            </form.Field>
          </Stack>
        </Stack>
        <form.Field name="phone">
          {(field) => (
            <CLMPhoneInput
              testID={buildTestId({ testID, suffix: ['input', 'phone'] })}
              label="Mobile Number (Optional)"
              placeholder="Phone"
              value={field.state.value}
              onChangeText={field.handleChange}
              onBlur={field.handleBlur}
              {...getErrorProps(field)}
            />
          )}
        </form.Field>
      </Stack>
    );
  },
);

IDNForm.displayName = 'IDNForm';
